/**
 * Customer-facing RMA list — the plant-context page a customer lands on
 * to view RMAs for their site group.
 *
 * Flat / DCE-lazy route: self-seeds customer + plant context via
 * useCustomer / useSiteGroup rather than relying on PlantHOC or a
 * ScopeContext provider above it. User-visible copy says "RMAs";
 * internal component names say "Return".
 *
 * Contains:
 * - CustomerReturnGridViewPage — the inner page component (named export)
 * - default export              — route component (default export for lazy import)
 */
import { Box } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import useCustomer from '@sophocles/hooks/useCustomer';
import useSiteGroup from '@sophocles/hooks/useSiteGroup';

import ReturnGrid from './components/ReturnGrid';

export function CustomerReturnGridViewPage(props) {
  const { t } = useTranslation();
  const { cust_id, sitegroup_id } = useParams();

  const { customer, loading: customerLoading } = useCustomer(Number(cust_id));
  const { siteGroup, loading: siteGroupLoading, curfew } = useSiteGroup(Number(sitegroup_id));

  // Suppress unused-variable lint — curfew is seeded for downstream
  // selectors even though this view does not consume it directly.
  void curfew;

  const { user } = useUser();
  void user;

  const loading = customerLoading || siteGroupLoading;

  // Readiness gate: hold until the site-group entity in the store
  // matches the URL param (mirrors PlantHOC's componentDidUpdate guard).
  const siteGroupReady = siteGroup && siteGroup.get('id') === Number(sitegroup_id);

  if (!siteGroupReady) {
    return <LoadingScreen />;
  }

  return (
    <AppNav flex notFullHeight loading={loading} page="plant_rmas" {...props}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'hidden',
          padding: '0 var(--page-px) var(--page-py)',
        }}
      >
        <TitleBar showBackButton title={t('support.rmaList', 'RMAs')} />
        <ReturnGrid variant="customer" customer={customer} siteGroupId={siteGroup.get('id')} />
      </Box>
    </AppNav>
  );
}

export default CustomerReturnGridViewPage;
